/**
 * Edit details (TMMPDM-0053). Shows only the descriptive fields the caller already intersected
 * with `editableFields`. A partial change set goes to `onSubmit`; `version` is passed through
 * and is `null` when the view has none (the caller omits it, including when the value is 0
 * only if the view has a version — 0 is sent).
 *
 * The title stays mounted through the exit transition. The form stays mounted when `open`
 * becomes false and remounts on the next open so a closed dialog does not keep a stale draft
 * into the next one. A version conflict keeps typed values until Reload.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePageBannerTarget } from './usePageBannerTarget';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { useDiscardChangesGuard, type DiscardChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import {
  ApiError,
  errorCopy,
  isOutcomeUnknown,
  isValidationFailed,
  validationFields,
} from '@/api/errors';
import { ErrorState } from './ErrorState';
import { useNotify } from './NotifyProvider';
import { SubmitButton } from './SubmitButton';

export interface EditDetailField {
  key: string;
  label: string;
  value: string;
  kind?: 'text' | 'multiline' | 'select' | 'money';
  maxLength?: number;
  required?: boolean;
  helperText?: string;
  options?: readonly { value: string; label: string }[];
}

export interface EditDetailsDialogProps {
  open: boolean;
  title?: string;
  /** The noun in the immutable sentence ("rate card", "agreement"). */
  entityLabel: string;
  fields: readonly EditDetailField[];
  /** `null` when the view has no version — distinct from version 0, which is sent. */
  version: number | null;
  validate?: (values: Record<string, string>) => Record<string, string>;
  onClose: () => void;
  onSubmit: (changed: Record<string, string>, version: number | null) => Promise<void>;
  /** Re-read. The parent bumps `reloadToken` after the read so the form resets. */
  onReload: () => Promise<void> | void;
  /** Background read after a save; must not reset a subsequently opened edit session. */
  onRefreshAfterSave?: () => Promise<void> | void;
  reloadToken: number;
  /** 502 outcome-unknown: close without a success toast. The parent re-reads. */
  onOutcomeUnknown?: () => void;
}

function isNameTaken(code: string): boolean {
  return code === 'NAME_TAKEN' || code.endsWith('_NAME_TAKEN');
}

function isImmutable(code: string): boolean {
  return code.endsWith('_IMMUTABLE');
}

function isVersionConflict(code: string): boolean {
  return code.endsWith('_VERSION_CONFLICT');
}

function immutableFields(error: ApiError): string {
  const fields = error.details.backend?.['fields'];
  if (!Array.isArray(fields)) return '';
  return fields.filter((field): field is string => typeof field === 'string').join(', ');
}

function nameTakenDetail(error: ApiError): string {
  const name = error.details.backend?.['name'];
  return typeof name === 'string' ? name : '';
}

function initialValues(fields: readonly EditDetailField[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of fields) values[field.key] = field.value;
  return values;
}

export function EditDetailsDialog(props: EditDetailsDialogProps) {
  const { t } = useCopy();
  const titleId = useId();
  const { open } = props;
  const discard = useDiscardChangesGuard(props.entityLabel, props.onClose);
  const [generation, setGeneration] = useState(0);
  const [prevOpen, setPrevOpen] = useState(open);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const bannerTarget = usePageBannerTarget();
  const bannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (refreshFailed && !open) bannerRef.current?.focus();
  }, [refreshFailed, open]);
  const refreshGeneration = useRef(0);
  const refreshAfterSave = async () => {
    const id = ++refreshGeneration.current;
    setRefreshBusy(true);
    try {
      await (props.onRefreshAfterSave ?? props.onReload)();
      if (refreshGeneration.current === id) setRefreshFailed(false);
    } catch {
      if (refreshGeneration.current === id) setRefreshFailed(true);
    } finally {
      if (refreshGeneration.current === id) setRefreshBusy(false);
    }
  };
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setGeneration((n) => n + 1);
  }
  const title = props.title ?? t('components.editDetails.title', 'Edit details');
  const banner = refreshFailed ? (
    <Alert
      ref={bannerRef}
      tabIndex={-1}
      severity="warning"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" disabled={refreshBusy} onClick={() => void refreshAfterSave()}>
          {t('components.editDetails.reload', 'Reload')}
        </Button>
      }
    >
      {t('components.editDetails.refreshFailed', "Saved. Couldn't refresh")}
    </Alert>
  ) : null;
  return (
    <>
      {bannerTarget ? createPortal(banner, bannerTarget) : banner}
      <Dialog
        open={open}
        onClose={discard.requestClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby={titleId}
        slotProps={{ transition: { onExited: () => bannerRef.current?.focus() } }}
      >
        <DialogTitle id={titleId}>{title}</DialogTitle>
        <EditDetailsForm
          key={generation}
          {...props}
          onAfterSave={refreshAfterSave}
          discard={discard}
        />
      </Dialog>
      {discard.dialog}
    </>
  );
}

function EditDetailsForm({
  fields,
  entityLabel,
  version,
  validate,
  onClose,
  onSubmit,
  onReload,
  reloadToken,
  onOutcomeUnknown,
  onAfterSave,
  discard,
}: EditDetailsDialogProps & { onAfterSave: () => Promise<void>; discard: DiscardChangesGuard }) {
  const { t } = useCopy();
  const [appliedToken, setAppliedToken] = useState(reloadToken);
  const [values, setValues] = useState(() => initialValues(fields));
  const [baseValues, setBaseValues] = useState(() => initialValues(fields));
  const [baseVersion, setBaseVersion] = useState(version);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [immutable, setImmutable] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A failed re-read is the ErrorState (Try again re-reads); `error` is only the save's.
  const [reloadFailure, setReloadFailure] = useState<{ cause: unknown } | null>(null);
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  if (reloadToken !== appliedToken) {
    setAppliedToken(reloadToken);
    setValues(initialValues(fields));
    setBaseValues(initialValues(fields));
    setBaseVersion(version);
    setFieldErrors({});
    setConflict(false);
    setError(null);
    setReloadFailure(null);
  }

  const setValue = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const reloadForm = async () => {
    setBusy(true);
    setError(null);
    setReloadFailure(null);
    try {
      await onReload();
    } catch (err: unknown) {
      setReloadFailure({ cause: err });
    } finally {
      setBusy(false);
    }
  };

  const localErrors = (): Record<string, string> => {
    const next: Record<string, string> = {};
    for (const field of fields) {
      const value = values[field.key] ?? '';
      if (field.required && value.trim() === '') {
        next[field.key] = t('components.editDetails.required', 'Required.');
      } else if (field.maxLength !== undefined && value.length > field.maxLength) {
        next[field.key] = t('components.editDetails.tooLong', 'At most {{max}} characters.', {
          max: field.maxLength,
        });
      }
    }
    return next;
  };

  // The guard asks before Esc, the backdrop or Cancel drops changes (TMM-B5).
  const dirty = fields.some((field) => (values[field.key] ?? '') !== baseValues[field.key]);
  const { setDirty } = discard;
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  const submit = async () => {
    const next = { ...localErrors(), ...(validate?.(values) ?? {}) };
    setFieldErrors(next);
    setError(null);
    if (Object.keys(next).length > 0) return;
    const changed: Record<string, string> = {};
    for (const field of fields) {
      const value = values[field.key] ?? '';
      if (value !== baseValues[field.key]) changed[field.key] = value;
    }
    if (Object.keys(changed).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await onSubmit(changed, baseVersion);
      notify.success(t('components.editDetails.saved', 'Details saved.'));
      onClose();
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        onOutcomeUnknown?.();
        onClose();
        return;
      }
      if (err instanceof ApiError && isNameTaken(String(err.code))) {
        const taken = nameTakenDetail(err);
        const message = t('components.editDetails.nameTaken', 'That name is already in use.');
        setFieldErrors({
          name: taken === '' ? message : `${message} (${taken})`,
        });
        return;
      }
      if (err instanceof ApiError && isImmutable(String(err.code))) {
        setImmutable(
          t(
            'components.editDetails.immutable',
            "{{fields}} can't be changed on a live {{entity}}. Use Copy to change.",
            { fields: immutableFields(err), entity: entityLabel },
          ),
        );
        await reloadForm();
        return;
      }
      if (err instanceof ApiError && isVersionConflict(String(err.code))) {
        setConflict(true);
        return;
      }
      if (isValidationFailed(err)) {
        // A refusal that marks no field this form shows still says something: the form's own line.
        const marked = validationFields(err, t);
        if (fields.some((field) => marked[field.key] !== undefined)) {
          setFieldErrors(marked);
        } else {
          setError(t('components.editDetails.saveError', "Couldn't save the details."));
        }
        return;
      }
      setError(
        errorCopy(err, t, t('components.editDetails.saveError', "Couldn't save the details.")),
      );
      return;
    } finally {
      setBusy(false);
    }
    // A saved write and its refresh are separate outcomes. Keep the page's saved rows on failure.
    await onAfterSave();
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {immutable ? <Alert severity="error">{immutable}</Alert> : null}
          {conflict ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  onClick={() => void reloadForm()}
                >
                  {t('components.editDetails.reload', 'Reload')}
                </Button>
              }
            >
              {t(
                'components.editDetails.conflict',
                'Someone else changed this. Reload to see their changes.',
              )}
            </Alert>
          ) : null}
          {reloadFailure ? (
            <ErrorState
              error={reloadFailure.cause}
              message={t('components.loadFailed', 'This could not be loaded.')}
              onRetry={() => void reloadForm()}
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {fields.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              value={values[field.key] ?? ''}
              onChange={(event) => setValue(field.key, event.target.value)}
              required={field.required}
              fullWidth
              select={field.kind === 'select'}
              multiline={field.kind === 'multiline'}
              {...(field.kind === 'multiline' ? { minRows: 3 } : {})}
              error={Boolean(fieldErrors[field.key])}
              helperText={fieldErrors[field.key] ?? field.helperText}
              slotProps={
                field.maxLength !== undefined
                  ? { htmlInput: { maxLength: field.maxLength } }
                  : undefined
              }
            >
              {field.kind === 'select'
                ? (field.options ?? []).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                : null}
            </TextField>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={discard.requestClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('components.editDetails.save', 'Save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
